import { useAtomValue } from 'jotai'
import { useCallback } from 'react'

import {
  handleFitBounds,
  handleFlyTo,
  handleFlyToFeature,
} from '@app/contexts/ModuleMapContexts/utils/location'
import { riskMapInstanceAtom, riskMapPaddingRefAtom } from '@app/risk-migration/atoms/riskMap.atoms'

import { FitToBoundsProps } from '@app/types/mapTypes'
import type { FlyTo } from '@app/contexts/ModuleMapContexts/utils/location'

export function useRiskMapNavigation() {
  const map = useAtomValue(riskMapInstanceAtom)
  const mapPaddingRef = useAtomValue(riskMapPaddingRefAtom)

  const getMapPadding = useCallback(
    (usePadding: boolean | undefined) =>
      usePadding || usePadding === undefined
        ? mapPaddingRef.current
        : { top: 0, right: 0, bottom: 0, left: 0 },
    [mapPaddingRef],
  )

  const flyTo = useCallback(
    (options: FlyTo) => {
      if (!map) return

      handleFlyTo({
        flyTo: {
          padding: getMapPadding(options.usePadding),
          ...options,
        },
        map,
      })
    },
    [map, getMapPadding],
  )

  const flyToFeature = useCallback(
    (feature: GeoJSON.Feature, flyToOptions?: Partial<FlyTo>) => {
      if (!map) return

      handleFlyToFeature({
        map,
        feature,
        flyToOptions: {
          padding: getMapPadding(flyToOptions?.usePadding),
          ...flyToOptions,
        },
      })
    },
    [map, getMapPadding],
  )

  const fitToBounds = useCallback(
    ({ boundingBox, usePadding }: Omit<FitToBoundsProps, 'map'> & { usePadding?: boolean }) => {
      if (!map) return

      handleFitBounds({
        boundingBox,
        map,
        padding: getMapPadding(usePadding),
      })
    },
    [map, getMapPadding],
  )

  return {
    flyTo,
    flyToFeature,
    fitToBounds,
    getMapPadding,
  }
}
